import React from 'react';
import {
  Calendar,
  Clock,
  Users,
  Mail,
  ExternalLink,
  ChevronRight,
  Linkedin,
} from 'lucide-react';
import { useGetEventsContent } from '../services';
import { Link } from 'react-router-dom';
import { Loading, Error } from '../shared/components';

const EventsPage: React.FC = () => {
  const { eventsData, pastEvents, upcomingEvents, isLoading, error } =
    useGetEventsContent();

  // Loading state
  if (isLoading) {
    return <Loading />;
  }

  // Error state
  if (error && !eventsData) {
    return <Error message={error} />;
  }

  return (
    <div className="py-6 sm:py-12">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-primary-500">
              {eventsData?.title || 'Events'}
            </h1>
            <div className="w-12 sm:w-16 h-0.5 rounded bg-primary-500"></div>
          </div>

          {/* Why Attend Section */}
          <div className="mb-10">
            <div className="flex items-start mb-4">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
              <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                {eventsData?.firstheader || 'Why Attend VLSI Club Events?'}
              </h2>
            </div>
            <p className="text-lg leading-relaxed text-secondary-700">
              {eventsData?.firstDesc ||
                'At the VLSI Club, we host a wide range of events designed to bridge the gap between the semiconductor industry and real-world VLSI design practices. From expert talks to summits, our events aim to spark curiosity and foster collaborations.'}
            </p>
          </div>

          {/* Upcoming Events */}
          <div className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  Upcoming Events
                </h2>
              </div>
              <Link
                to="/events/upcoming"
                className="inline-flex items-center text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity duration-200 text-primary-500"
              >
                View All Upcoming Events
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {upcomingEvents.map((event) => (
                <div
                  key={event.id}
                  className="bg-gray-100 p-4 sm:p-6 rounded-lg border-l-4 border-l-primary-500 cursor-pointer transition-all duration-200 hover:shadow-lg hover:bg-gray-50"
                  onClick={() =>
                    event.webinarLink &&
                    window.open(event.webinarLink, '_blank')
                  }
                >
                  <h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4 text-secondary-700">
                    {event.title}
                  </h3>

                  <div className="space-y-2 sm:space-y-3">
                    {(event.dateStr || event.day) && (
                      <div className="flex items-start">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                        <span className="text-xs sm:text-sm text-secondary-700">
                          {event.dateStr}
                          {event.day && ` (${event.day})`}
                        </span>
                      </div>
                    )}
                    {(event.startTimeStr || event.endTimeStr) && (
                      <div className="flex items-start">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                        <span className="text-xs sm:text-sm text-secondary-700">
                          {event.startTimeStr}
                          {event.endTimeStr && ` - ${event.endTimeStr}`}
                        </span>
                      </div>
                    )}
                    {event.speaker && (
                      <div className="flex items-start">
                        <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                        <div className="flex-1">
                          <span className="text-xs sm:text-sm text-secondary-700">
                            Speaker: {event.speaker}
                          </span>
                          {event.linkedIn && (
                            <a
                              href={event.linkedIn}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center ml-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin className="w-3 h-3 sm:w-4 sm:h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {event.desc && (
                    <p className="text-xs sm:text-sm mt-3 text-secondary-700 line-clamp-2">
                      {event.desc}
                    </p>
                  )}
                </div>
              ))}

              {/* Show placeholder if no upcoming events */}
              {upcomingEvents.length === 0 && (
                <div className="bg-gray-100 p-4 sm:p-6 rounded-lg col-span-full text-center">
                  <p className="text-lg text-primary-500">
                    No upcoming events scheduled
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Past Events */}
          <div className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
              <div className="flex items-start">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  Past Events
                </h2>
              </div>
              <Link
                to="/events/past"
                className="inline-flex items-center text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity duration-200 text-primary-500"
              >
                View All Past Events
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {pastEvents.map((event) => (
                <div
                  key={event.id}
                  className="bg-gray-100 p-4 sm:p-6 rounded-lg border-l-4 border-l-primary-500 cursor-pointer transition-all duration-200 hover:shadow-lg hover:bg-gray-50"
                  onClick={() =>
                    event.webinarLink &&
                    window.open(event.webinarLink, '_blank')
                  }
                >
                  <h3 className="text-lg font-semibold mb-3 text-secondary-700">
                    {event.title}
                  </h3>

                  <div className="space-y-2 mb-3">
                    {event.dateStr && (
                      <div className="flex items-start">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                        <span className="text-xs sm:text-sm text-secondary-700">
                          {event.dateStr}
                        </span>
                      </div>
                    )}
                    {(event.startTimeStr || event.endTimeStr) && (
                      <div className="flex items-start">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                        <span className="text-xs sm:text-sm text-secondary-700">
                          {event.startTimeStr}
                          {event.endTimeStr && ` - ${event.endTimeStr}`}
                        </span>
                      </div>
                    )}
                    {event.speaker && (
                      <div className="flex items-start">
                        <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                        <div className="flex-1">
                          <span className="text-xs sm:text-sm text-secondary-700">
                            Speaker: {event.speaker}
                          </span>
                          {event.linkedIn && (
                            <a
                              href={event.linkedIn}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center ml-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin className="w-3 h-3 sm:w-4 sm:h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {event.desc && (
                    <p className="text-sm mb-3 text-secondary-700 line-clamp-2">
                      {event.desc}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    {event.webinarLink ? (
                      <button className="text-xs underline hover:opacity-80 text-primary-500 hover:text-primary-600 transition-colors duration-200">
                        View Recording
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">
                        No recording
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {/* Show placeholder if no past events */}
              {pastEvents.length === 0 && (
                <div className="bg-gray-100 p-4 sm:p-6 rounded-lg col-span-full text-center">
                  <p className="text-lg text-primary-500">
                    No past events available
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Participate Section */}
          <div className="bg-gray-100 p-4 sm:p-6 rounded-lg">
            <h2 className="text-lg sm:text-2xl font-semibold mb-4 text-primary-500">
              {eventsData?.secoundheader ||
                'Want to Participate or Co-Host, or Sponsor an Event?'}
            </h2>
            <p className="text-lg mb-6 text-secondary-700">
              {eventsData?.secoundDesc ||
                "If you're interested in attending, co-hosting, or sponsoring an event with us:"}
            </p>

            <div className="space-y-4">
              <div className="flex items-start">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-3 mt-0.5 flex-shrink-0 text-primary-500" />
                <span className="text-sm sm:text-base text-secondary-700">
                  Email:{' '}
                  <a
                    href="mailto:contact@vlsiclub.org"
                    className="text-primary-500 underline hover:opacity-80"
                  >
                    contact@vlsiclub.org
                  </a>
                </span>
              </div>
              <div className="flex items-start">
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 mr-3 mt-0.5 flex-shrink-0 text-primary-500" />
                <span className="text-sm sm:text-base text-secondary-700">
                  Follow us on{' '}
                  <button className="text-primary-500 underline hover:opacity-80 cursor-pointer">
                    LinkedIn
                  </button>{' '}
                  for updates
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
